import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import CancelIcon from '@mui/icons-material/Cancel';
import instance from '../../utils/axiosConfig';

function PendingVerifications() {
  const [distributors, setDistributors] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [verifyDialog, setVerifyDialog] = useState({
    open: false,
    distributor: null
  });
  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    distributor: null
  });

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      const response = await instance.get('/api/moderator-management/pending-verifications');
      setDistributors(response.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar verificaciones pendientes'
      });
    }
  };

  const handleVerifyClick = (distributor) => {
    setVerifyDialog({
      open: true,
      distributor: distributor
    });
  };

  const handleVerifyCancel = () => {
    setVerifyDialog({
      open: false,
      distributor: null
    });
  };

  const handleVerifyConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/verify-distributor/${verifyDialog.distributor._id}`);
      setMessage({ type: 'success', text: 'Distribuidor verificado exitosamente' });
      fetchPendingVerifications();
      handleVerifyCancel();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al verificar distribuidor'
      });
    }
  };

  const handleRejectClick = (distributor) => {
    setRejectDialog({
      open: true,
      distributor: distributor
    });
  };

  const handleRejectCancel = () => {
    setRejectDialog({
      open: false,
      distributor: null
    });
  };

  const handleRejectConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/reject-verification/${rejectDialog.distributor._id}`);
      setMessage({ type: 'success', text: 'Verificación rechazada exitosamente' });
      fetchPendingVerifications();
      handleRejectCancel();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al rechazar verificación'
      });
    }
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Información del Distribuidor</TableCell>
              <TableCell>Plan Actual</TableCell>
              <TableCell>Fecha de Registro</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {distributors.map((distributor) => (
              <TableRow key={distributor._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {distributor.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Razón Social: {distributor.governmentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {distributor.CUIT}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {distributor.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Teléfono: {distributor.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dirección: {distributor.address}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {distributor.subscription ? (
                      <MilitaryTechIcon 
                        sx={{ 
                          color: distributor.subscription?.planId === 'plata' ? '#C0C0C0' : 
                                 distributor.subscription?.planId === 'oro' ? '#FFD700' : 
                                 distributor.subscription?.planId === 'diamante' ? '#42e3e3' : 
                                 'error.main',
                          fontSize: '2.2rem'
                        }} 
                      />
                    ) : (
                      <Chip
                        label="Sin Plan"
                        sx={{
                          color: 'error.main',
                          border: '1px solid',
                          borderColor: 'error.main',
                          backgroundColor: 'white',
                        }}
                      />
                    )}
                   
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(distributor.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'warning.main',
                      fontWeight: 500 
                    }}
                  >
                    Sin Verificar
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleVerifyClick(distributor)}
                      color="success"
                      title="Verificar distribuidor"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRejectClick(distributor)}
                      color="error"
                      title="Rechazar verificación"
                    >
                      <CancelIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {distributors.length === 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          No hay distribuidores pendientes de verificación
        </Typography>
      )}

      <Dialog
        open={verifyDialog.open}
        onClose={handleVerifyCancel}
      >
        <DialogTitle>Confirmar Verificación</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Estás seguro que deseas verificar al siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {verifyDialog.distributor?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {verifyDialog.distributor?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {verifyDialog.distributor?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Plan:</strong> {verifyDialog.distributor?.subscription?.planId}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email:</strong> {verifyDialog.distributor?.email}
            </Typography>
            <Typography variant="body2">
              <strong>Teléfono:</strong> {verifyDialog.distributor?.phone}
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main">
            Esta acción otorgará la insignia de verificación al distribuidor.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVerifyCancel}>
            Cancelar
          </Button>
          <Button onClick={handleVerifyConfirm} color="success" variant="contained">
            Verificar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={handleRejectCancel}
      >
        <DialogTitle>Confirmar Rechazo de Verificación</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Estás seguro que deseas rechazar la verificación del siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {rejectDialog.distributor?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {rejectDialog.distributor?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {rejectDialog.distributor?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Plan:</strong> {rejectDialog.distributor?.subscription?.planId?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email:</strong> {rejectDialog.distributor?.email}
            </Typography>
            <Typography variant="body2">
              <strong>Teléfono:</strong> {rejectDialog.distributor?.phone}
            </Typography>
          </Box>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            Esta acción:
          </Typography>
          <Box component="ul" sx={{ mt: 0, color: 'error.main' }}>
            <Typography component="li" variant="body2">
              Marcará al distribuidor como verificación fallida
            </Typography>
            <Typography component="li" variant="body2">
              Removerá su plan actual
            </Typography>
            <Typography component="li" variant="body2">
              Lo agregará a la lista de reembolsos pendientes
            </Typography>
          </Box>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel}>
            Cancelar
          </Button>
          <Button onClick={handleRejectConfirm} color="error" variant="contained">
            Rechazar Verificación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PendingVerifications; 